import { AlertTriangle, CheckCircle2, ShieldX } from 'lucide-react';

import type { GateDecisionDto } from '../../lib/api/endpoints';
import { cn } from '../../lib/utils';

interface RulesGateResultProps {
  decision: GateDecisionDto | null;
  isLoading?: boolean;
}

export function RulesGateResult({ decision, isLoading = false }: RulesGateResultProps) {
  if (isLoading) {
    return (
      <div className="rounded border border-border bg-surface p-4 text-sm text-text-muted">
        Checking gate...
      </div>
    );
  }

  if (!decision) {
    return (
      <div className="rounded border border-border bg-surface p-4 text-sm text-text-muted">
        Enter a plan to preview the rules gate.
      </div>
    );
  }

  const tone =
    decision.decision === 'APPROVED'
      ? 'approved'
      : decision.decision === 'WARN'
        ? 'warn'
        : 'blocked';
  const Icon =
    decision.decision === 'APPROVED'
      ? CheckCircle2
      : decision.decision === 'WARN'
        ? AlertTriangle
        : ShieldX;
  const hardBlocks = decision.ruleResults.filter((result) => result.status === 'BLOCK');
  const softWarnings = decision.ruleResults.filter((result) => result.status === 'WARN');

  return (
    <section
      className={cn(
        'space-y-4 rounded border p-4',
        tone === 'approved' && 'border-success/40 bg-success/10',
        tone === 'warn' && 'border-warn/40 bg-warn/10',
        tone === 'blocked' && 'border-danger/40 bg-danger/10',
      )}
    >
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5" aria-hidden="true" />
        <h2 className="text-lg font-semibold">{decision.decision}</h2>
      </div>
      {hardBlocks.length > 0 ? (
        <div>
          <h3 className="text-sm font-semibold text-danger">Hard blocks</h3>
          <ul className="mt-1 space-y-1 text-sm text-red-200">
            {hardBlocks.map((result) => (
              <li key={result.key}>{result.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {softWarnings.length > 0 ? (
        <div>
          <h3 className="text-sm font-semibold text-warn">Soft warnings</h3>
          <ul className="mt-1 space-y-1 text-sm text-amber-100">
            {softWarnings.map((result) => (
              <li key={result.key}>{result.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {decision.reasons.length === 0 ? (
        <p className="text-sm text-text-muted">All enabled rules passed.</p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {decision.ruleResults.map((result) => (
          <span
            key={`${result.key}-${result.status}`}
            className={cn(
              'rounded border px-2 py-1 text-xs font-medium',
              result.status === 'PASS' && 'border-success/40 bg-success/10',
              result.status === 'WARN' && 'border-warn/40 bg-warn/10',
              result.status === 'BLOCK' && 'border-danger/40 bg-danger/10',
            )}
          >
            {result.key}: {formatMetric(result.value)} / {formatMetric(result.threshold)}
            {result.message ? ` - ${result.message}` : ''}
          </span>
        ))}
      </div>
    </section>
  );
}

function formatMetric(value: number | null): string {
  if (value === null) return 'n/a';
  if (Math.abs(value) <= 1) return `${(value * 100).toFixed(1)}%`;
  return value.toFixed(2);
}
